// src/features/dashboard/components/EmployeeActiveHoursReport/ActiveHoursHeader.tsx

import {
  getEmployeeCode,
  getEmployeeName,
} from '../../../auth/utils/Login/authStorage';

interface Props {
  activeToday: number;
  sevenDayAvg: number;
  utilisation: number;
}

/* ============================================================================
   FORMAT HOURS
   ============================================================================ */

const formatHours = (
  value: number,
): string => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return '0.0000h';
  }

  return `${numericValue.toFixed(4)}h`;
};

/* ============================================================================
   COMPONENT
   ============================================================================ */

const ActiveHoursHeader = ({
  activeToday,
  sevenDayAvg,
  utilisation,
}: Props) => {
  /* ==========================================================================
     EMPLOYEE CODE
     ========================================================================== */

  const employeeCode =
    getEmployeeCode() || '';

  /* ==========================================================================
     EMPLOYEE NAME
     ========================================================================== */

  const employeeName =
    getEmployeeName() ||
    employeeCode ||
    'Employee';

  /* ==========================================================================
     SAFE VALUES
     ========================================================================== */

  const safeActiveToday =
    Number.isFinite(
      Number(activeToday),
    )
      ? Number(activeToday)
      : 0;

  const safeSevenDayAvg =
    Number.isFinite(
      Number(sevenDayAvg),
    )
      ? Number(sevenDayAvg)
      : 0;

  const safeUtilisation =
    Number.isFinite(
      Number(utilisation),
    )
      ? Number(utilisation)
      : 0;

  /* ==========================================================================
     ACTIVE HOURS
     ========================================================================== */

  const activeHours =
    formatHours(
      safeActiveToday,
    );

  /* ==========================================================================
     7 DAY AVERAGE %
     ========================================================================== */

  const averageDifference =
    safeSevenDayAvg > 0
      ? (
          (
            safeActiveToday -
            safeSevenDayAvg
          ) /
          safeSevenDayAvg
        ) * 100
      : 0;

  const averagePercentage =
    Math.abs(
      averageDifference,
    );

  const averageStatus =
    safeActiveToday >=
    safeSevenDayAvg
      ? 'above'
      : 'below';

  /* ==========================================================================
     UTILISATION
     ========================================================================== */

  const utilisationText =
    `${Math.round(
      safeUtilisation,
    )}%`;

  /* ==========================================================================
     RENDER
     ========================================================================== */

  return (
    <div
      className="
        flex
        flex-wrap
        items-center
        gap-3
      "
    >
      {/* ======================================================================
          TITLE
          ====================================================================== */}

      <h2
        className="
          text-[14px]
          font-semibold
          text-[#002B5B]
        "
      >
        Active Hours · Today {activeHours}
      </h2>

      {/* ======================================================================
          EMPLOYEE DETAILS
          ====================================================================== */}

      <span
        className="
          rounded-full
          bg-[#EEF4FF]
          px-3
          py-1
          text-[11px]
          font-medium
          text-[#2563EB]
        "
        title={employeeCode}
      >
        {employeeName} ·{' '}
        {averagePercentage.toFixed(0)}%
        {' '}
        {averageStatus} 7-day avg ·
        {' '}
        Utilisation {utilisationText}
      </span>
    </div>
  );
};

export default ActiveHoursHeader;